/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Alert, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { CookieComplianceAcceptButton, ICookieComplianceViewProps } from '@msdyn365-commerce-modules/cookie-compliance';
import { ICookieComplianceProps, ICookieComplianceResources } from '../definition-extensions/cookie-compliance.ext.props.autogenerated';

const CookieComplianceView: React.FC<ICookieComplianceViewProps & ICookieComplianceProps<ICookieComplianceResources>> = props => {
    const { CookieComplianceBanner, AlertProps, Content, text, links, config, context } = props;

    const [isVisible, setIsVisible] = React.useState<boolean>(true);

    React.useEffect(() => {
        if (context.request.cookies.isConsentGiven()) {
            setIsVisible(false);
            return;
        }

        if (!config.hideOnScroll) {
            return;
        }

        const handleScroll = () => {
            _onDismiss();
        };
        window.addEventListener('scroll', handleScroll, { once: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [config.hideOnScroll]);

    if (!isVisible) return null;

    const _onDismiss = (): void => {
        if (context && context.request && context.request.cookies) {
            context.request.cookies.setConsentCookie();
            location.reload();
        }
        setIsVisible(false);
    };

    const theme = config.useDarkTheme ? 'dark' : 'light';
    const moduleProps = {
        ...CookieComplianceBanner,
        class: `${CookieComplianceBanner.className || ''} ${theme}`
    };

    return (
        <Module {...moduleProps}>
            <Alert className={AlertProps.className} color={AlertProps.color} fade={AlertProps.fade}>
                <Node {...Content}>
                    <button className='ms-cookie-compliance__content__main__dismiss-button' onClick={_onDismiss}>
                        &#10005;
                    </button>
                    <div className='ms-cookie-compliance__content__labels'>
                        {text}
                        {links}
                    </div>
                    <div className='ms-cookie-compliance__content__main'>
                        <CookieComplianceAcceptButton
                            onClose={_onDismiss}
                            acceptCookiesButtonText={config.acceptButtonText || 'Confirm'}
                            acceptCookiesButtonAriaLabel={config.acceptButtonText || 'Confirm'}
                        />
                    </div>
                </Node>
            </Alert>
        </Module>
    );
};
export default CookieComplianceView;
